/**
 * Pure helper for deciding whether a presigned S3 URL has expired, based on its
 * own `X-Amz-Date` / `X-Amz-Expires` query params. Lives in utils (no React
 * dependency) so it can be reused by hooks and plain helpers alike.
 *
 * Returns `true` when the URL lacks the S3 expiry params — callers that handle
 * non-S3 (e.g. CDN) URLs should account for that (see `@utils/avatarUrlCache`).
 *
 * `bufferSec` shrinks the window a URL is considered valid, so a "should I
 * proactively refetch" decision can treat a near-expiry URL as already
 * expired (the default 2-hour buffer, unchanged for existing callers). Pass
 * `0` for an exact/real expiry check — required by `shouldReplaceAvatarSource`
 * below, which decides whether to discard an image that's currently
 * displaying successfully; using the buffered default there would replace a
 * still-genuinely-valid signature in the final 2 hours of its life and
 * reintroduce the TA-10111 flicker this is meant to avoid.
 */
export function isUrlExpired(
  url: string,
  now: Date,
  bufferSec = 2 * 60 * 60
): boolean {
  const expiresAt = getExpiryTimestamp(url);
  // No parseable S3 expiry params at all — either the URL genuinely lacks
  // them (e.g. a CDN thumbnail) or they're present but malformed (invalid
  // date, non-numeric expiry). Either way there's no trustworthy signal that
  // the URL is still good, so this function's long-standing contract is to
  // report "expired"; callers with a real non-S3 URL story account for that
  // separately (see `shouldReplaceAvatarSource` below and `avatarUrlCache`).
  if (expiresAt == null) return true;

  return expiresAt - bufferSec * 1000 < now.getTime();
}

/**
 * The real-world timestamp (ms since epoch) at which `url`'s S3 signature
 * expires, or `null` when the URL has no `X-Amz-Date`/`X-Amz-Expires` params,
 * or when those params can't be parsed into a valid date/number. Exported so
 * callers can schedule work for exactly that moment (see the expiry-triggered
 * promotion effects in Avatar.tsx / CachedImage.tsx) instead of only
 * re-checking on the next incidental re-render.
 */
export function getExpiryTimestamp(url: string): number | null {
  const params = new URLSearchParams(url.split('?')[1]);
  const amzDate = params.get('X-Amz-Date');
  const amzExpires = params.get('X-Amz-Expires');

  if (!amzDate || !amzExpires) return null;

  const requestTime = new Date(
    `${amzDate.slice(0, 4)}-${amzDate.slice(4, 6)}-${amzDate.slice(
      6,
      8
    )}T${amzDate.slice(9, 11)}:${amzDate.slice(11, 13)}:${amzDate.slice(
      13,
      15
    )}Z`
  );
  // `Number()` (unlike `parseInt`) rejects a value with a valid numeric
  // *prefix* but trailing garbage (e.g. "604800garbage" — parseInt happily
  // returns 604800) — PR review found this would have let a corrupted
  // X-Amz-Expires slip past the malformed-value guard below.
  const expiresSec = Number(amzExpires);
  if (Number.isNaN(requestTime.getTime()) || !Number.isFinite(expiresSec)) {
    return null;
  }

  return requestTime.getTime() + expiresSec * 1000;
}

/** Whether `url` carries EITHER S3 presigned-URL expiry param — distinguishes
 * a genuinely non-S3 URL (e.g. a CDN thumbnail, neither param present, no
 * expiry story) from an S3 URL whose signature is malformed or partial (only
 * one of the two params present). PR review: requiring *both* params here
 * would treat a corrupted signature missing just one param the same as a
 * plain CDN URL and never replace it — `getExpiryTimestamp` already requires
 * both to compute a real expiry, so a partial match still correctly falls
 * through to `isUrlExpired` treating it as expired below. */
function hasS3ExpiryParams(url: string): boolean {
  const params = new URLSearchParams(url.split('?')[1]);
  return params.has('X-Amz-Date') || params.has('X-Amz-Expires');
}

/**
 * TA-10914: shared decision for whether a newly-arrived avatar URL should
 * replace the one currently being displayed.
 *
 * S3 presigned URLs rotate their query-string signature on every server
 * response, so comparing full strings would treat every rotation of the same
 * object as a new image and reload/flash needlessly (TA-10111). Comparing by
 * path alone fixes that, but then never replaces a signature once it expires
 * — the avatar goes grey and stays that way. This holds both guarantees: a
 * same-object rotation is ignored while the held signature is still valid,
 * and is accepted once it has genuinely expired (or nothing is held yet, or
 * the candidate points at a different object).
 */
export function shouldReplaceAvatarSource(
  heldUrl: string | null | undefined,
  candidateUrl: string | null | undefined,
  now: Date
): boolean {
  if (candidateUrl === heldUrl) return false;
  if (!heldUrl) return true;
  if (!candidateUrl) return true;
  const heldPath = heldUrl.split('?')[0];
  const candidatePath = candidateUrl.split('?')[0];
  if (heldPath !== candidatePath) return true;
  // A non-S3 same-path URL (e.g. a CDN thumbnail, which has no expiry
  // params at all and doesn't expire) already cleared the identical-string
  // check above; a differing query string here isn't evidence of
  // staleness, so keep it stable instead of always replacing it (which
  // would defeat the no-flicker guarantee for non-presigned avatars). A
  // same-path URL that DOES carry S3 params — even malformed ones — still
  // goes through the real expiry check below, where `isUrlExpired` treats
  // unparseable params as expired rather than trusting them indefinitely.
  if (!hasS3ExpiryParams(heldUrl)) return false;
  return isUrlExpired(heldUrl, now, 0);
}

/**
 * Whether `incoming` is a renewed signature for the same file as `existing`:
 * `existing` is expired (or inside the proactive-refetch buffer) and `incoming`
 * outlives it. Presigned URLs for one file share a path and differ only in the
 * query-string signature, so callers that dedupe on the path (the
 * `File.thumbnail` cache merge) use this to avoid pinning an expired signature
 * forever. A held URL with no S3 expiry params at all (e.g. CDN) has no
 * signature to go stale and is never replaced; one with malformed params is
 * treated as expired (same rule as `shouldReplaceAvatarSource`), so a good
 * incoming signature can replace it. The default refetch buffer is deliberate:
 * a fetch that lands inside it must be able to replace the held URL, otherwise
 * callers that refetch in that window would repeat the fetch to no effect. The
 * displayed source is still gated at exact expiry by `shouldReplaceAvatarSource`.
 */
export function isRenewedSignature(
  existing: string,
  incoming: string,
  now: Date = new Date()
): boolean {
  if (!hasS3ExpiryParams(existing)) return false;
  const incomingExpiry = getExpiryTimestamp(incoming);
  if (incomingExpiry == null) return false;

  const existingExpiry = getExpiryTimestamp(existing);
  if (existingExpiry == null) return true; // malformed held signature
  return isUrlExpired(existing, now) && incomingExpiry > existingExpiry;
}
